import jwtDecode from 'jwt-decode';
import { useDispatch, useSelector } from 'react-redux';
import { createContext, useEffect, useCallback, useState } from 'react';
import { AuthIdToken, AuthUserType, AwsCognitoJwtContextType } from './types';
import { externalLogout, isTokenExpirationValid, refreshToken, setSession } from './utils';
import { getUser, importUser, getTenants } from 'api-client';
import TenantRepository from 'common/infrastructure/tenant/repository';
import OrganizationRepository from 'common/infrastructure/organization/repository';
import {
  queryClient,
  restoreAndResumeSavedMutations,
} from 'common/infrastructure/remoteState/persistQueryClient';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { setAuth, AuthPayloadTypes } from './auth/redux';
import { RootStoreType } from 'redux/rootReducer';
import AssetClassRepository from 'assetClass/infrastructure/assetClass/repository';
import {
  usePostHogEvents,
  identifyUser,
  setTenantGroup,
  resetPosthog,
} from 'common/infrastructure/cookies/usePostHogEvents';
import {
  deriveIsInternal,
  deriveHasAllTenantAccess,
} from 'common/infrastructure/cookies/analyticsIdentity';
import { VITE_ENV } from 'config';
import { JwtClaimVerificationService } from './jwtClaimVerification';
import { retryUntilCondition } from '@planitgeo/treeplotter-domain/lib/infrastructure/retry/retry-utils';
import {
  EVENT_USER_LOGIN_ATTEMPTED,
  EVENT_USER_LOGIN_COMPLETED,
  EVENT_USER_LOGIN_FAILED,
  EVENT_USER_LOGOUT,
} from 'common/infrastructure/analytics/events';

export const AuthContext = createContext<AwsCognitoJwtContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const dispatch = useDispatch();
  const authState = useSelector((reduxState) => (reduxState as RootStoreType).auth);
  const tenantState = useSelector((reduxState) => (reduxState as RootStoreType).tenants);
  const { trackEvent } = usePostHogEvents({ tenant_id: undefined, user_id: undefined });
  const [isLoading, setIsLoading] = useState(false);

  const initialize = useCallback(async () => {
    try {
      let idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';
      const initialTokenValid = idToken ? isTokenExpirationValid(idToken) : false;

      loggerService.log(LoggerLevel.DEBUG, '[AUTH_INIT] Starting authentication initialization', {
        hasToken: !!idToken,
        tokenValid: initialTokenValid,
        appVersion: typeof window !== 'undefined' ? window.appVersion : 'unknown',
      });

      if (idToken && !isTokenExpirationValid(idToken)) {
        loggerService.log(
          LoggerLevel.DEBUG,
          '[AUTH_INIT] Token expired, attempting refresh during initialization'
        );
        idToken = await refreshToken(idToken);
      }

      if (idToken && isTokenExpirationValid(idToken)) {
        setSession(idToken);

        const decodedIdToken = jwtDecode<AuthIdToken>(idToken);

        const user = {
          username: decodedIdToken.username,
          sub: decodedIdToken.sub,
        };

        // Identify user in PostHog as early as possible in the session.
        // The ID token carries no email (Cognito suppresses the claim), so
        // is_internal cannot be derived here; usePostHogEvents backfills it
        // once the user record resolves.
        identifyUser({
          user_id: decodedIdToken.sub,
          has_all_tenant_access: deriveHasAllTenantAccess(decodedIdToken.tp3_tenant_ids),
        });

        // Verify JWT claims and handle any mismatches
        await JwtClaimVerificationService.verifyAndHandleMismatches();

        // invalidate tenant request
        await TenantRepository.refetchTenants(queryClient);
        if (tenantState.currentTenantPid) {
          await AssetClassRepository.refetchAssetClasses(tenantState.currentTenantPid);
          await OrganizationRepository.refetchOrganizations(
            queryClient,
            tenantState.currentTenantPid
          );
        }

        dispatch(
          setAuth({
            type: AuthPayloadTypes.INITIAL,
            data: {
              isAuthenticated: true,
              user,
            },
          })
        );
      } else {
        loggerService.log(
          LoggerLevel.DEBUG,
          '[AUTH_INIT] No valid token after initialization - setting unauthenticated state',
          {
            hadToken: !!idToken,
            tokenValid: idToken ? isTokenExpirationValid(idToken) : false,
            wasAuthenticated: authState.isAuthenticated,
          }
        );

        if (authState.isAuthenticated && authState.user) {
          trackEvent(EVENT_USER_LOGOUT, {
            user_uuid: authState.user.sub,
            logout_method: 'token_expired',
          });
        }

        dispatch(
          setAuth({
            type: AuthPayloadTypes.INITIAL,
            data: {
              isAuthenticated: false,
              user: null,
            },
          })
        );
      }
    } catch (error) {
      const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';
      const tokenValid = idToken ? isTokenExpirationValid(idToken) : false;

      loggerService.log(
        LoggerLevel.DEBUG,
        '[AUTH_INIT] Error during initialization - setting unauthenticated state',
        {
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          hasToken: !!idToken,
          tokenValid,
          wasAuthenticated: authState.isAuthenticated,
        }
      );

      // Track automatic logout due to error
      trackEvent(EVENT_USER_LOGOUT, {
        logout_method: 'automatic_error',
        error_type: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });

      dispatch(
        setAuth({
          type: AuthPayloadTypes.INITIAL,
          data: {
            isAuthenticated: false,
            user: null,
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initialize();
  }, [initialize]);

  // Update PostHog tenant group when the current tenant, or its TP2 id,
  // changes. A restored session rehydrates currentTenantPid from persisted
  // Redux before the tenants query resolves, so keying only on the pid would
  // group that session with the TP2 id permanently missing — the pid never
  // changes afterwards to re-run this.
  const currentTenantTp2Id = TenantRepository.useCurrentTenantTp2Id();

  useEffect(() => {
    try {
      const currentTenantId = tenantState.currentTenantPid;
      if (currentTenantId) {
        setTenantGroup(currentTenantId, currentTenantTp2Id);
      }
    } catch (error) {
      loggerService.log(LoggerLevel.ERROR, 'Failed to set PostHog tenant group', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantState.currentTenantPid, currentTenantTp2Id]);

  const login = async (idToken: string) => {
    // Track login attempt
    trackEvent(EVENT_USER_LOGIN_ATTEMPTED, {
      login_method: VITE_ENV.isLocalhost ? 'jwt_token' : 'cognito',
    });

    setIsLoading(true);
    setSession(idToken);

    const decodedIdToken = jwtDecode<AuthIdToken>(idToken);

    try {
      // Restore any paused mutations saved before the previous logout/cache-clear.
      // Called here because onSuccess in RemoteStateProvider already fired on page load
      // (before this login), at which point the JWT was not yet in localStorage.
      await restoreAndResumeSavedMutations();

      const importUserPromise = importUser({
        id: decodedIdToken.sub,
      });

      TenantRepository.refetchTenants(queryClient); // fire off in parallel

      await importUserPromise; // Wait for user import to complete before getting user details

      // Wait for tenants to be available to ensure full user import is complete
      const tenantsResult = await retryUntilCondition(
        async () => {
          try {
            const tenants = await getTenants();
            return tenants;
          } catch (error) {
            loggerService.log(LoggerLevel.DEBUG, 'getTenants attempt failed, will retry', {
              userId: decodedIdToken.sub,
              error: error instanceof Error ? error.message : 'Unknown error',
            });
            return null;
          }
        },
        (result) => result !== null && Array.isArray(result) && result.length > 0, // Success when we get at least one tenant
        {
          maxRetries: 5,
          retryDelay: 100,
          backoffMultiplier: 2,
          logContext: {
            userId: decodedIdToken.sub,
            action: 'get_tenants_after_import',
          },
        }
      );

      if (!tenantsResult.success) {
        loggerService.log(
          LoggerLevel.WARN,
          'Failed to get tenants after retries, continuing anyway',
          {
            userId: decodedIdToken.sub,
          }
        );
      }

      const user = await getUser({ userId: decodedIdToken.sub });
      const { email } = user;
      const userData: AuthUserType = {
        displayName: 'PlanIt Geo User',
        email,
        role: 'admin',
        username: decodedIdToken.username,
        sub: decodedIdToken.sub,
      };

      dispatch(
        setAuth({
          type: AuthPayloadTypes.LOGIN,
          data: {
            user: userData,
          },
        })
      );

      // Verify JWT claims after successful login
      await JwtClaimVerificationService.verifyAndHandleMismatches();

      // Identify user in PostHog after successful login. Prefer the tenants we
      // just fetched over the query cache, which may not be written yet here.
      const currentTenantPid = TenantRepository.getCurrentTenantPid();
      const tp2TenantId =
        tenantsResult.result?.find((t) => t.id === currentTenantPid)?.tp2TenantId ??
        TenantRepository.getCurrentTenantTp2Id();

      identifyUser({
        user_id: userData.sub,
        tenant_id: currentTenantPid,
        tp2_tenant_id: tp2TenantId,
        is_internal: deriveIsInternal(email),
        has_all_tenant_access: deriveHasAllTenantAccess(decodedIdToken.tp3_tenant_ids),
      });

      trackEvent(EVENT_USER_LOGIN_COMPLETED, {
        user_uuid: userData.sub,
        login_method: VITE_ENV.isLocalhost ? 'jwt_token' : 'cognito',
      });

      setIsLoading(false);
    } catch (error) {
      trackEvent(EVENT_USER_LOGIN_FAILED, {
        error_type: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        login_method: VITE_ENV.isLocalhost ? 'jwt_token' : 'cognito',
      });

      loggerService.log(LoggerLevel.ERROR, "Couldn't log in", error);
      setIsLoading(false);
      return;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const register = async (data: any) => {
    // TODO: implement register
  };

  const logout = async () => {
    if (authState.user) {
      trackEvent(EVENT_USER_LOGOUT, {
        user_uuid: authState.user.sub,
        logout_method: 'user_initiated',
      });
    }

    dispatch(
      setAuth({
        type: AuthPayloadTypes.LOGOUT,
      })
    );
    resetPosthog();
    await externalLogout({ skipLogoutWhenOffline: false });
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        method: 'awsCognitoJwt',
        isLoading,
        register,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
